import {
  AgentMessageType,
  ExternalEntityType,
} from '~/api/sylvanus/typedefs.autogen';
import { IconType } from '~/eds/src/types/IconType';
import { SearchQuery } from '~/features/advanced-search/types';
import { SearchEntity } from '~/features/ask-anything/types/chatbot';
import { Entity } from '~/types/Entity';
import { HighlightPositionResponse } from '~/types/Highlight';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

export type CitationEntity = ExternalEntityType;

/**
 * A document attached to a Request (ticket_v2), resolved from the citation's link rather than
 * `entity.id` -- the backend still tags these `entity.type: 'document_id'` with the legacy pilot
 * document id, which can't resolve a Request-scoped document (it needs the Request id, the K2
 * document id, and the version number, all embedded only in the link). Not an `ExternalEntityType`:
 * this is a UI-only dispatch shape for `getCitationEntity`/`resolveCitation`.
 */
export type TicketV2DocumentCitationEntity = {
  type: 'ticket_v2_document';
  ticketId: string;
  documentId: string;
  version: number;
};

/** What `getCitationEntity` may resolve a citation to, and what `resolveCitation` accepts. */
export type ResolvableCitationEntity =
  | Entity<CitationEntity>
  | TicketV2DocumentCitationEntity
  | null;

/** Outcome of a full-search skill turn, as reported by the search agent. */
export type SearchStatus =
  | 'ok'
  | 'zero_results'
  | 'error'
  | 'no_search'
  | 'unknown';

/** One structured result emitted by the full-search skill. */
export type SearchResultItem = {
  /** The entity's handler ID. */
  id: string;
  /** The entity type, e.g. "document" or "ticket". */
  entity: string;
  /** Human-readable display name. */
  name: string;
  /** Full in-app URL resolved server-side, or `null` for entities with no document view. */
  url: Nullable<string>;
};

export const ANCHOR_ORDERS = ['earliest', 'latest'] as const;

export type AnchorOrder = (typeof ANCHOR_ORDERS)[number];

/** Where an answer is being shown. Decides how much it says, and labels its analytics. */
export enum AnswerSurface {
  Chat = 'chat',
  Search = 'search',
}

type AnswerAnchorTopResult = {
  /** Display name of the highest-ranked result. */
  name: string;
  /** The result's ID, matching an entry in `searchResultItems` used to render a hyperlink. */
  id?: string;
  /** Evisearch filter ID used to rank the results. */
  sortedBy?: string;
  /** That field's label, as shown in the filter panel (e.g. "Expiration Date"). */
  sortedByLabel: string;
  /** Which end of the ranking field `name` sits at, and so whether the copy reads soonest or most recent. */
  order: AnchorOrder;
  /** ISO calendar day the result holds for the ranking field, when it has one. */
  date?: string;
};

/**
 * One grounded fact about a full-search result set, resolved server-side from the whole set.
 * Present only when a fact was available; its absence is what suppresses the answer copy.
 */
export type AnswerAnchor = {
  /** What kind of answer the question called for: enum, count or list. */
  intentCategory: Nullable<string>;
  topResult: AnswerAnchorTopResult;
  /** ISO calendar days spanning the ranking field across the whole result set. */
  dateRange?: { earliest: string; latest: string };
};

export type ChatMessageContext = {
  searchQuery?: SearchQuery;
  searchEntity?: SearchEntity;
  selectedIds?: string[];
  entitiesInView?: Entity<CitationEntity>[];
  messageType?: AgentMessageType;
};

export type Message = {
  /** Message ID. */
  id: Uuid;
  /** Message text */
  text: string;
  /** Message timestamp (a JS Date) */
  timestamp: Date;
  /** Type of user who authored the message. */
  userType: UserType;
  /** Optional set of response suggestions sometimes included in a bot message. */
  choices?: string[];
  /** Displays loading icon */
  isStreaming?: boolean;
  /** Optional set of source objects sometimes included in a bot message. */
  sources?: MessageSource[];
  /** Contains information about Citation on the text. */
  citations?: Citation[];
  /** Optional user feedback rating the message. */
  feedbackDetails?: Nullable<FeedbackDetails>;
  /** Message type: 'query' for typed messages, 'command' for clicked suggestions */
  messageType?: 'query' | 'command';
  /** Persisted scope from the API message_context payload. */
  messageContext?: ChatMessageContext;
  /** Constructed search query from the full-search skill, for "View in Search" CTA. */
  constructedSearchQuery?: string;
  /** Outcome status of a full-search skill turn. */
  searchStatus?: SearchStatus;
  /** Total number of results matched by the full-search skill (may exceed the number shown). */
  searchResultCount?: number;
  /** Up to a page of structured result items, rendered as hyperlinked names. */
  searchResultItems?: SearchResultItem[];
  answerAnchor?: AnswerAnchor;
};

export type Citation = {
  key: string;
  highlights: MessageSource[];
  entity: Nullable<Entity<CitationEntity>>;
  entityName: Nullable<string>;
  link: Nullable<string>;
  tooltip: Nullable<string>;
  originalQuote: Nullable<string>;
};

export interface ResolvedCitationEntity {
  path?: string;
  link: string;
  lastModified: Date;
  fileName?: string;
}

export type MessageSource = {
  id: Uuid;
  coordinates: Coordinates[];
  htmlTokens: number[];
  citationKey?: string;
};

interface Coordinates {
  source: string;
  coordinates: HighlightPositionResponse;
  token_ids: number[];
}

export type FeedbackDetails = {
  value: Nullable<boolean>;
  category?: Nullable<string>;
  comment?: string;
};

export type UserType = 'current' | 'other';

export interface MessageAction {
  icon: IconType;
  tooltip: string;
  onClick: (message: Message, messages?: Message[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export type ActiveContext = {
  icon: IconType;
  text: string;
  onReset?: () => void;
  disableReset?: boolean;
};
